/**
 * What "See preview" opens on the report wait.
 *
 * One component for both designs rather than two copies, because the rule it
 * enforces is the same in both and is easy to get wrong in one of them: §3
 * forbids presenting a reconstructed interface as a product screenshot, so
 * the caption is driven by the preview's own `illustrative` flag and is never
 * written at the call site.
 *
 * The images live in `public/campaign/` and are referenced by URL, not
 * imported: they are ~100KB each and only one is ever opened, so bundling
 * them would make every visitor pay for all three on a screen most people
 * never expand.
 */

import React, { useEffect, useRef, useState } from 'react';
import CampaignModal from './CampaignModal';
import { CAPABILITY_PREVIEWS } from './audienceCopy';

const REVEAL_MS = 320;

export default function CapabilityPreview({ panelKey, title, skin = 'cmp', revealed = true }) {
  const preview = CAPABILITY_PREVIEWS[panelKey];
  const [open, setOpen] = useState(false);
  // `mounted` outlives the close by the length of the fold. Unmounting on
  // the same tick collapses an empty box, which reads as a flicker rather
  // than a fold; the open class itself can follow `revealed` directly,
  // because the wrapper is never unmounted and so always has a 0fr frame
  // to animate away from.
  const [mounted, setMounted] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (revealed) { setMounted(true); return undefined; }
    timer.current = setTimeout(() => setMounted(false), REVEAL_MS);
    return () => clearTimeout(timer.current);
  }, [revealed]);

  // A panel with no preview defined shows nothing at all rather than an
  // empty frame — the same rule the demo button follows.
  if (!preview) return null;

  const mk = skin === 'mk';
  const revealClass = mk ? 'mk-reveal' : 'cmp-reveal';
  return (
    <div
      className={`${revealClass}${revealed ? ` ${revealClass}--open` : ''}`}
      // Hidden the instant it starts to fold: from then on what is still
      // on screen is decoration, not content.
      aria-hidden={revealed ? undefined : 'true'}
    >
      {/* The WRAPPER always renders, even closed and empty. Unmounting it
          meant that on the next open it was created and classed in the same
          commit, so there was no 0fr frame to animate from and the fold
          only ever worked once. The content inside it is what comes and
          goes. */}
      <div className={mk ? 'mk-preview' : 'cmp-preview'}>
        {mounted && (
        <>
        {/* These are captures of a dense application shrunk into a ~500px
            card, so the thumbnail shows the shape of the product and not its
            text. Enlarging happens in place: a new tab would drop someone out
            of a funnel that is mid-run, onto a bare JPEG with no way back. */}
        <button
          type="button"
          className={mk ? 'mk-preview-trigger' : 'cmp-preview-trigger'}
          onClick={() => setOpen(true)}
          aria-label={`Enlarge: ${preview.alt}`}
        >
          <img
            className={mk ? 'mk-preview-img' : 'cmp-preview-img'}
            src={preview.src}
            alt={preview.alt}
            width={preview.width}
            height={preview.height}
            loading="lazy"
          />
        </button>
        <p className={mk ? 'mk-note' : 'cmp-meta'}>
          {preview.caption}{' '}
          <button
            type="button"
            className={mk ? 'mk-link' : 'cmp-link-btn'}
            onClick={() => setOpen(true)}
          >
            Enlarge
          </button>
        </p>

        <CampaignModal
          isOpen={open}
          title={title || 'Preview'}
          onClose={() => setOpen(false)}
          skin={skin}
          maxWidth={1180}
          testId="capability-preview-modal"
        >
          <img
            className="campaign-preview-full"
            src={preview.src}
            alt={preview.alt}
            width={preview.width}
            height={preview.height}
          />
          <p className={mk ? 'mk-note' : 'cmp-meta'} style={{ marginTop: 12 }}>{preview.caption}</p>
        </CampaignModal>
        </>
        )}
      </div>
    </div>
  );
}
